import { Item } from '../../settings/items/types/item.types';
import { ItemLocation, Location } from '../../settings/items/types/item-location.types';

export interface ItemInventory {
  id: string;
  transactionDate: string;
  transactionNumber: string;
  type: 'INCOMING' | 'DISPENSED_TO_PATIENT' | 'DISPOSED' | 'TRANSFER' | 'IN_WARD' | 'ADJUSTMENT';
  fromLocationId?: string;
  fromLocation?: Location;
  toLocationId?: string;
  toLocation?: Location;
  totalStockValue: number;
  remarks?: string;
  status: 'DRAFT' | 'APPROVED' | 'CANCELLED';
  approvedBy?: string;
  approvedAt?: string;
  isActive: boolean;
  isDeleted: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  deletedBy?: string;
  details?: ItemInventoryDetail[];
  reorderLevel?: number;
}

export interface ItemInventoryDetail {
  id: string;
  itemInventoryId: string;
  itemInventory?: ItemInventory;
  itemId: string;
  item?: Item;
  itemLocationId?: string;
  itemLocation?: ItemLocation;
  batchNumber: string;
  expiryDate?: string;
  quantity: number;
  costPrice: number;
  totalItemValue: number;
  balance: number;
  mode: 'CREDIT' | 'DEBIT';
  deductedFromId?: string;
  deductedFrom?: ItemInventoryDetail;
  deductions?: ItemInventoryDetail[];
  isActive: boolean;
  isDeleted: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  deletedBy?: string;
}

export interface ItemInventoryFormData {
  transactionDate: string;
  type: 'INCOMING' | 'DISPENSED_TO_PATIENT' | 'DISPOSED' | 'TRANSFER' | 'IN_WARD' | 'ADJUSTMENT';
  fromLocationId?: string | null;
  toLocationId?: string | null;
  remarks?: string;
  status?: 'DRAFT' | 'APPROVED' | 'CANCELLED';
  details: ItemInventoryDetailFormData[];
}

export interface ItemInventoryDetailFormData {
  itemId: string;
  itemLocationId?: string;
  batchNumber: string;
  expiryDate?: string;
  quantity: number;
  costPrice: number;
  mode: 'CREDIT' | 'DEBIT';
  deductedFromId?: string;
  appointmentOrderId?: string; // Link to appointment order for partial dispensing tracking
}

export interface ItemInventoryFilterParams {
  page?: number;
  limit?: number;
  type?: string;
  status?: string;
  fromLocationId?: string;
  toLocationId?: string;
  includeDetails?: boolean;
}

export interface TransactionTypeConfig {
  type: 'INCOMING' | 'DISPENSED_TO_PATIENT' | 'DISPOSED' | 'TRANSFER' | 'IN_WARD' | 'ADJUSTMENT';
  label: string;
  description: string;
  icon: string;
  color: 'primary' | 'success' | 'error' | 'warning' | 'info';
  requiresFromLocation: boolean;
  requiresToLocation: boolean;
  defaultMode: 'CREDIT' | 'DEBIT';
}

export const TRANSACTION_TYPES: TransactionTypeConfig[] = [
  {
    type: 'INCOMING',
    label: 'Incoming Stock',
    description: 'Stock received from suppliers',
    icon: 'TrendingUp',
    color: 'success',
    requiresFromLocation: false,
    requiresToLocation: true,
    defaultMode: 'CREDIT'
  },
  {
    type: 'DISPENSED_TO_PATIENT',
    label: 'Dispensed to Patient',
    description: 'Stock given to patients',
    icon: 'PersonAdd',
    color: 'primary',
    requiresFromLocation: true,
    requiresToLocation: false,
    defaultMode: 'DEBIT'
  },
  {
    type: 'DISPOSED',
    label: 'Disposed',
    description: 'Stock disposed or wasted',
    icon: 'Delete',
    color: 'error',
    requiresFromLocation: true,
    requiresToLocation: false,
    defaultMode: 'DEBIT'
  },
  {
    type: 'TRANSFER',
    label: 'Transfer',
    description: 'Stock moved between locations',
    icon: 'SwapHoriz',
    color: 'info',
    requiresFromLocation: true,
    requiresToLocation: true,
    defaultMode: 'DEBIT'
  },
  {
    type: 'IN_WARD',
    label: 'In Ward',
    description: 'Stock moved to ward/department',
    icon: 'LocalHospital',
    color: 'warning',
    requiresFromLocation: true,
    requiresToLocation: true,
    defaultMode: 'DEBIT'
  },
  {
    type: 'ADJUSTMENT',
    label: 'Adjustment',
    description: 'Stock adjustments/corrections',
    icon: 'Tune',
    color: 'warning',
    requiresFromLocation: false,
    requiresToLocation: true,
    defaultMode: 'CREDIT'
  }
];
